import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import { Store } from "@ngxs/store";
import { API_BASE_URI } from "../../http/api";
import { Tag } from "../../types/EvaluationLibrary/tag";
import { DetailRequestBuilder, DetailRequest } from "../../http/detail";
import { Filter, FilterBuilder } from "../../http/filter";
import { Collection } from "../../http/collection";
import { SearchRequest, SearchResponse } from '../../http/search';
import { Observable, throwError } from "rxjs";
import { catchError, tap } from "rxjs/operators";
import { Dot } from "@core/utils";
import { DuplicateLabelError } from "./weighted-char.service";

export class TagCreated {
    static readonly type = "[TagService] Tag Created";
    constructor(public tag: Tag) {}
}

export class TagUpdated {
    static readonly type = "[TagService] Tag Updated";
    constructor(public tag: Tag) {}
}

export class TagDeleted {
    static readonly type = "[TagService] Tag Deleted";
    constructor(public tag: Tag) {}
}

@Injectable()
export class TagService {

    constructor(
        private _http: HttpClient,
        private _store: Store,
        @Inject(API_BASE_URI) private _baseUri: string,
    ) {}

    get(tagKey: string, detail?: DetailRequest): Observable<Tag> {
        const params = detail ? DetailRequestBuilder.queryParams(detail) : {};
        return this._http.get<Tag>(`${this._baseUri}/evaluation-library/tag/${tagKey}`, {params});
    }

    create(model: Partial<Tag>) {
        return this._http.post<Tag>(`${this._baseUri}/evaluation-library/tag`, model)
            .pipe(
                this.catchDupliacteLabelError(),
                tap(tag => this._store.dispatch(new TagCreated(tag)))
            );
    }

    update(key: string, model: Partial<Tag>) {
        return this._http.put<Tag>(`${this._baseUri}/evaluation-library/tag/${key}`, model)
            .pipe(
                this.catchDupliacteLabelError(),
                tap(tag => this._store.dispatch(new TagUpdated(tag)))
            );
    }

    delete(key: string) {
        return this._http.delete<Tag>(`${this._baseUri}/evaluation-library/tag/${key}`)
            .pipe(tap(tag => this._store.dispatch(new TagDeleted(tag))));
    }

    query(filter: Filter, detail?: DetailRequest): Observable<Collection<Tag>> {
        const params = {
            ...FilterBuilder.queryParams(filter),
            ...DetailRequestBuilder.queryParams(detail),
        };

        return this._http.get<Collection<Tag>>(`${this._baseUri}/evaluation-library/tag`, {params});
    }

    search(search: SearchRequest) {
        return this._http.get<SearchResponse<Tag>>(`${this._baseUri}/evaluation-library/tag/search`, {params: <any>search});
    }

    private catchDupliacteLabelError() {
        return catchError<Tag, Observable<Tag>>(e => {
            if (e instanceof HttpErrorResponse && e.status === 422
                && String(Dot.get(e.error, 'errors.label', '')).includes('taken') ) {
                    return throwError(new DuplicateLabelError(e));
            }

            return throwError(e);
        });
    }
}